import { Crown, Sparkles } from 'lucide-react';

/**
 * MembershipBadge - Displays Gold or Elite membership badge
 * Gold: Gold crown icon with gold coloring
 * Elite: Sparkling Electric Blue badge (includes "Creator" tier)
 */
export const MembershipBadge = ({ membershipStatus, size = 'md', showLabel = true }) => {
  const sizes = {
    sm: { icon: 'w-3 h-3', text: 'text-xs', padding: 'px-2 py-0.5' },
    md: { icon: 'w-4 h-4', text: 'text-sm', padding: 'px-3 py-1' },
    lg: { icon: 'w-5 h-5', text: 'text-base', padding: 'px-4 py-1.5' }
  };

  const sizeConfig = sizes[size] || sizes.md;
  
  // Normalize membership status - "Creator" tier is Elite
  const normalizedStatus = membershipStatus?.toLowerCase();
  const isElite = normalizedStatus === 'elite' || normalizedStatus === 'creator';
  const isGold = normalizedStatus === 'gold';

  if (isElite) {
    return (
      <span 
        className={`inline-flex items-center gap-1 ${sizeConfig.padding} rounded-full ${sizeConfig.text} font-bold animate-pulse`}
        style={{ 
          backgroundColor: 'rgba(0, 191, 255, 0.2)', 
          color: '#00BFFF',
          boxShadow: '0 0 10px rgba(0, 191, 255, 0.3)'
        }}
        data-testid="elite-badge"
      >
        <Sparkles className={sizeConfig.icon} />
        {showLabel && 'ELITE'}
      </span>
    );
  }
  
  if (isGold) {
    return (
      <span 
        className={`inline-flex items-center gap-1 ${sizeConfig.padding} rounded-full ${sizeConfig.text} font-bold`}
        style={{ 
          backgroundColor: 'rgba(255, 215, 0, 0.2)', 
          color: '#FFD700' 
        }}
        data-testid="gold-badge"
      >
        <Crown className={sizeConfig.icon} />
        {showLabel && 'GOLD'}
      </span>
    );
  }

  return null;
};

/**
 * EliteRequiredPopup - Shows coming soon notice for Elite-only features
 */
export const EliteRequiredPopup = ({ feature = 'this feature', onUpgrade, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 bg-zinc-800">
            <Sparkles className="w-8 h-8 text-cyan-400/50" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-white mb-2">Coming Soon</h2>
          <p className="text-zinc-400">
            {feature} is part of our Elite tier which is currently under development. We're building dedicated servers for live auction streaming.
          </p>
        </div>
        
        <div className="space-y-3">
          <div className="w-full py-3 px-4 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 text-zinc-500" />
            <span className="text-zinc-400 font-medium">Live Auctions Coming Soon</span>
          </div>
          <button
            onClick={onClose}
            className="w-full py-3 px-4 rounded-lg font-medium text-zinc-400 hover:text-white transition-colors border border-zinc-700 hover:border-zinc-600"
            data-testid="close-popup-btn"
          >
            Got it
          </button>
        </div>
        
        <p className="text-zinc-500 text-xs mt-4">
          Stay tuned for updates! Elite tier will include Live Auctions, Combined Shipping, and more.
        </p>
      </div>
    </div>
  );
};

export default MembershipBadge;
